// Resources/Private/JavaScript/Controllers/HeaderController.js

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["menuToggle", "dropdownToggle", "dropdown"];

    connect() {
        this.keyboardInteraction = false;
        this.hoverPointerQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    }

    trackPointerInput() {
        this.keyboardInteraction = false;
    }

    trackKeyboardInput() {
        this.keyboardInteraction = true;
    }

    toggleMenu(event) {
        event.preventDefault();

        const isOpen = this.menuToggleTarget.getAttribute("aria-expanded") === "true";
        this.setMenuOpen(!isOpen);
    }

    handleDropdownClick(event) {
        const trigger = event.currentTarget;
        const dropdown = this.dropdownFor(trigger);

        if (!dropdown || event.detail === 0 || this.hoverPointerQuery.matches) {
            return;
        }

        const isOpen = trigger.getAttribute("aria-expanded") === "true";

        if (isOpen) {
            return;
        }

        event.preventDefault();
        this.closeDropdowns(trigger);
        this.setDropdownOpen(trigger, dropdown, true);
    }

    openDropdownOnHover(event) {
        if (!this.hoverPointerQuery.matches) {
            return;
        }

        this.openDropdownForItem(event.currentTarget);
    }

    closeDropdownOnHover(event) {
        if (!this.hoverPointerQuery.matches || event.currentTarget.contains(document.activeElement)) {
            return;
        }

        this.closeDropdownForItem(event.currentTarget);
    }

    openDropdownOnFocus(event) {
        if (!this.keyboardInteraction || event.currentTarget.contains(event.relatedTarget)) {
            return;
        }

        this.openDropdownForItem(event.currentTarget);
    }

    closeDropdownOnFocusOut(event) {
        if (event.currentTarget.contains(event.relatedTarget)) {
            return;
        }

        this.closeDropdownForItem(event.currentTarget);
    }

    closeFromOutside(event) {
        if (this.element.contains(event.target)) {
            return;
        }

        this.closeDropdowns();
        this.setMenuOpen(false);
    }

    closeOnEscape(event) {
        if (event.key !== "Escape") {
            return;
        }

        const openDropdownTrigger = this.dropdownToggleTargets.find(
            (trigger) => trigger.getAttribute("aria-expanded") === "true",
        );

        if (openDropdownTrigger) {
            event.preventDefault();
            this.closeDropdowns();
            openDropdownTrigger.focus();
            return;
        }

        if (this.menuToggleTarget.getAttribute("aria-expanded") === "true") {
            event.preventDefault();
            this.setMenuOpen(false);
            this.menuToggleTarget.focus();
        }
    }

    setMenuOpen(isOpen) {
        this.menuToggleTarget.setAttribute("aria-expanded", String(isOpen));
        this.menuToggleTarget.setAttribute(
            "aria-label",
            isOpen ? "Hauptnavigation schließen" : "Hauptnavigation öffnen",
        );

        if (!isOpen) {
            this.closeDropdowns();
        }
    }

    closeDropdowns(exceptTrigger = null) {
        this.dropdownToggleTargets.forEach((trigger) => {
            if (trigger === exceptTrigger) {
                return;
            }

            const dropdown = this.dropdownFor(trigger);
            if (dropdown) {
                this.setDropdownOpen(trigger, dropdown, false);
            }
        });
    }

    setDropdownOpen(trigger, dropdown, isOpen) {
        trigger.setAttribute("aria-expanded", String(isOpen));
        dropdown.hidden = !isOpen;
    }

    openDropdownForItem(item) {
        const trigger = this.triggerForItem(item);
        const dropdown = trigger ? this.dropdownFor(trigger) : null;

        if (!trigger || !dropdown) {
            return;
        }

        this.closeDropdowns(trigger);
        this.setDropdownOpen(trigger, dropdown, true);
    }

    closeDropdownForItem(item) {
        const trigger = this.triggerForItem(item);
        const dropdown = trigger ? this.dropdownFor(trigger) : null;

        if (trigger && dropdown) {
            this.setDropdownOpen(trigger, dropdown, false);
        }
    }

    triggerForItem(item) {
        return item.querySelector(":scope > [data-header-target='dropdownToggle']");
    }

    dropdownFor(trigger) {
        const dropdownId = trigger.getAttribute("aria-controls");
        return this.dropdownTargets.find((dropdown) => dropdown.id === dropdownId);
    }
}
